import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useFetch, useFetchMutation } from '../hooks/useFetchAuth';

function CarsData() {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dataType, setDataType] = useState('telematique');
  
  const { data: fetchedData, isLoading: isLoadingData, refetch: fetchAvailableData } = useFetch(
    ['cars-data-availability'],
    `${API_BASE_URL}/api/cars-data-availability`
  );
  
  const availableData = fetchedData || [];
  
  const { mutateAsync: uploadCarsData } = useFetchMutation();

  const [dateRange, setDateRange] = useState([null, null]);
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setError(null);
      setSuccessMessage(null);
    }
  };

  // Refresh data after successful upload
  useEffect(() => {
    if (successMessage) {
      fetchAvailableData();
    }
  }, [successMessage]);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFileType(droppedFile)) {
      setFile(droppedFile);
      setError(null);
      setSuccessMessage(null);
    }
  };

  const isValidFileType = (file) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    return file && validTypes.includes(file.type);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError("Veuillez sélectionner un fichier Excel.");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('start_date', dateRange[0].getTime()/1000);
    formData.append('end_date', dateRange[1].getTime()/1000);
    
    try {
      await uploadCarsData({
        url: `${API_BASE_URL}/api/upload-cars-data/${dataType}`,
        method: 'POST',
        body: formData
      });
      setSuccessMessage("Fichier chargé et traité avec succès 🎊");
      setFile(null);
      setDateRange([null, null]);
    } catch (err) {
      console.error("Upload error:", err);
      const errorDetail = err.response?.detail || err.message || "Une erreur s'est produite lors du traitement du fichier.";
      setError(typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : errorDetail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
          Importation des données flotte de véhicule
        </h1>
        
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Chargement du fichier Excel</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-4">
              <h3 className="text-md font-medium mb-2 text-gray-900 dark:text-white">Type de données</h3>
              <div className="flex flex-wrap gap-2">
                {['telematique', 'Switch conducteur', 'Facture Ayvens', 'Carburant Total'].map((type) => (
                  <button
                    disabled={isLoading || type !== 'telematique'}
                    key={type}
                    type="button"
                    onClick={() => setDataType(type)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200
                      ${dataType === type
                        ? 'bg-blue-600 text-white dark:bg-blue-700'
                        : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          <div className="mb-4 flex flex-col gap-2">
            <h3 className="text-md font-medium mb-2 text-gray-900 dark:text-white">Rentrer la période de <span className="font-bold text-blue-600 dark:text-blue-500">{dataType}</span></h3>
            <DatePicker
              onChange={(dateRange) => setDateRange(dateRange)}
              highlightDates={[new Date()]}
              selectsRange={true}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              isClearable={true}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={isLoading}
              dateFormat="dd/MM/yyyy"
            />
          </div>
            <div 
              className="text-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileInput"
                className="hidden"
                accept=".xls,.xlsx"
                onChange={handleFileChange}
              />
              <label
                htmlFor="fileInput"
                className="cursor-pointer inline-block bg-blue-600 dark:bg-blue-700 text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors duration-200"
              >
                {file ? 'Charger un autre fichier' : 'Choisir un fichier'}
              </label>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                ou glissez-déposez votre fichier ici
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                (formats acceptés: .xls, .xlsx)
              </p>
            </div>
            
            {file && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">Fichier sélectionné:</h3>
                <p className="text-gray-600 dark:text-gray-300">Nom: {file.name}</p>
                <p className="text-gray-600 dark:text-gray-300">Taille: {formatFileSize(file.size)}</p>
              </div>
            )}
            
            {error && (
              <div className="bg-red-100 dark:bg-red-900 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-4 rounded-lg">
                <p>{typeof error === 'object' ? JSON.stringify(error) : error}</p>
              </div>
            )}
            
            {successMessage && (
              <div className="bg-green-100 dark:bg-green-900 border-l-4 border-green-500 text-green-700 dark:text-green-300 p-4 rounded-lg">
                <p>{successMessage}</p>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading || !file || !dateRange[0] || !dateRange[1]}
                className={`px-6 py-2 rounded-lg text-white font-semibold
                  ${!isLoading && file && dateRange[0] && dateRange[1]
                    ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
                    : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'}
                  transition-colors duration-200 flex items-center justify-center`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Traitement en cours...
                  </>
                ) : 'Traiter le fichier'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Available Cars Data Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            Données disponibles
          </h2>
          
          {isLoadingData ? (
            <div className="flex justify-center p-4">
              <svg className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : availableData.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300 text-center py-4">
              Aucune donnée disponible pour le moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date de début
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date de fin
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {availableData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(item.start_date * 1000).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(item.end_date * 1000).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={fetchAvailableData}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Rafraîchir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CarsData;